'use client'

import * as React from 'react'
import { Toast } from '@base-ui/react/toast'

import type { ToastActionElement, ToastProps } from '@/components/ui/toast'

export const toastManager = Toast.createToastManager()

interface ToastCustomData {
  className?: string
  style?: React.CSSProperties
}

type ToasterToast = ToastProps & {
  id: string
  title?: React.ReactNode
  description?: React.ReactNode
  action?: ToastActionElement
  duration?: number
}

type ToastInput = Omit<ToasterToast, 'id'>

type ToastManagerActionProps = Omit<
  React.ComponentProps<'button'>,
  'ref'
>

function mapActionProps(
  action: ToastActionElement | undefined,
): ToastManagerActionProps | undefined {
  if (!action) return undefined

  const { className, children, ...buttonProps } = action.props

  return {
    ...buttonProps,
    children,
    className: typeof className === 'string' ? className : undefined,
  } as ToastManagerActionProps
}

function mapToastOptions({
  title,
  description,
  variant,
  duration,
  className,
  style,
  action,
}: ToastInput) {
  return {
    title,
    description,
    type: variant === 'destructive' ? 'destructive' : undefined,
    timeout: duration,
    data: {
      className,
      style,
    } satisfies ToastCustomData,
    actionProps: mapActionProps(action),
  }
}

function toast({ ...props }: ToastInput) {
  const id = toastManager.add(mapToastOptions(props))

  const update = (nextProps: ToasterToast) =>
    toastManager.update(id, mapToastOptions({ ...props, ...nextProps }))

  const dismiss = () => toastManager.close(id)

  return {
    id,
    dismiss,
    update,
  }
}

function useToast() {
  return {
    toasts: [] as ToasterToast[],
    toast,
    dismiss: (toastId?: string) => toastManager.close(toastId),
  }
}

export { useToast, toast }
